<!-- Notifications bell -->
<button
	mat-icon-button
	[matMenuTriggerFor]="notificationsMenu"
	[matBadge]="(unreadCount$ | async) || null"
	[matBadgeHidden]="((unreadCount$ | async) || 0) === 0"
	matBadgeColor="warn"
	matBadgeSize="small"
	[matTooltip]="'userNotifications.title' | transloco"
	[matTooltipShowDelay]="listItemTooltipShowDelay"
	(click)="refreshList()"
>
	<mat-icon
		fontSet="fas"
		fontIcon="fa-bell"
		class="text-xl text-slate-600 transition-colors hover:text-primary"
	></mat-icon>
</button>

<mat-menu
	xPosition="before"
	#notificationsMenu="matMenu"
	class="!min-w-0 !max-w-none !overflow-hidden !rounded-2xl !p-0"
>
	<div
		class="w-110 overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200"
		(click)="$event.stopPropagation()"
		(keydown)="$event.stopPropagation()"
	>
		<!-- Header -->
		<div class="flex items-center justify-between px-5 py-4">
			<div class="flex min-w-0 items-center gap-2">
				<span class="text-secondary font-semibold text-slate-900">
					{{ 'userNotifications.title' | transloco }}
				</span>
				<span
					*ngIf="((unreadCount$ | async) || 0) > 0"
					class="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
				>
					{{ unreadCount$ | async }} unread
				</span>
			</div>

			<div class="flex items-center gap-1">
				<button
					mat-button
					color="primary"
					class="!min-h-0 !px-2 !py-1 !text-sm !font-medium"
					*ngIf="((unreadCount$ | async) || 0) > 0"
					(click)="markAllRead()"
				>
					{{ 'userNotifications.markAllRead' | transloco }}
				</button>

				<app-dialog-menu
					[class.invisible]="(hasRead$ | async) === false"
					color="warn"
					text="userNotifications.deleteAllRead"
					[message]="'userNotifications.deleteAllReadConfirmMessage'"
					[ngButtonClass]="'!min-h-0 !border-0 !px-2 !py-1 !text-sm !font-medium'"
					(confirmEvent)="deleteAllRead()"
				></app-dialog-menu>
			</div>
		</div>

		<mat-divider></mat-divider>

		<ng-container *ngIf="items$ | async as items; else loading">
			<ng-container *ngIf="items.length > 0; else empty">
				<div class="max-h-[430px] overflow-y-auto py-2">
					<button
						*ngFor="let item of items"
						mat-menu-item
						class="group relative mx-2 mb-1 !flex !h-auto !min-h-0 !w-[calc(100%-1rem)] !items-start !whitespace-normal !rounded-xl !px-0 !py-0 transition-colors hover:!bg-slate-50"
						[ngClass]="{
							'!bg-blue-50 hover:!bg-blue-100': !item.isRead,
							'!bg-transparent': item.isRead
						}"
						(click)="onClick(item)"
					>
						<!-- Unread indicator (hidden on hover) -->
						<span
							*ngIf="!item.isRead"
							class="absolute right-3 top-3 z-10 h-2 w-2 rounded-full bg-primary transition-opacity group-hover:opacity-0"
						></span>

						<!-- Delete button (shown on hover, replaces unread indicator) -->
						<button
							mat-icon-button
							class="!absolute !right-1 !top-1 !z-10 !h-7 !w-7 opacity-0 transition-opacity group-hover:opacity-100"
							[matTooltip]="'userNotifications.delete' | transloco"
							[matTooltipShowDelay]="listItemTooltipShowDelay"
							(click)="deleteItem(item, $event)"
						>
							<mat-icon
								fontSet="fas"
								fontIcon="fa-times"
								class="!text-xs !text-slate-400 hover:!text-slate-700"
							></mat-icon>
						</button>

						<div class="relative flex w-full gap-3 px-3 py-3">
							<!-- Unread accent -->
							<span
								*ngIf="!item.isRead"
								class="absolute left-0 top-3 h-[calc(100%-1.5rem)] w-1 rounded-r-full bg-primary"
							></span>

							<ng-container [ngSwitch]="item.type">
								<!-- UserTaggedInComment -->
								<ng-container *ngSwitchCase="NotificationType.UserTaggedInComment">
									<!-- Icon bubble -->
									<div
										class="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
										[ngClass]="{
											'bg-primary/10 text-primary': !item.isRead,
											'bg-slate-100 text-slate-400': item.isRead
										}"
									>
										<mat-icon
											fontSet="fas"
											fontIcon="fa-comment-dots"
											class="!text-sm"
										></mat-icon>
									</div>

									<!-- Content -->
									<div class="min-w-0 flex-1">
										<ng-container *ngIf="getTaggedPayload(item) as payload">
											<span
												class="line-clamp-2 text-sm font-semibold leading-snug"
												[ngClass]="{
													'text-slate-950': !item.isRead,
													'text-slate-700': item.isRead
												}"
											>
												{{
													'userNotifications.userTaggedInCommentTitle' | transloco: { taggedByUserFullName: payload.taggedByUserFullName }
												}}
											</span>

											<!-- Customer name -->
											<span
												*ngIf="payload.customerName"
												class="mt-1 inline-flex max-w-full items-center rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600"
											>
												<span class="truncate">
													{{ payload.customerName }}
												</span>
											</span>
										</ng-container>

										<!-- Comment/body -->
										<p
											*ngIf="item.body"
											class="mt-1 line-clamp-2 text-xs leading-relaxed text-slate-500"
										>
											{{ item.body }}
										</p>

										<!-- Timestamp -->
										<span class="mt-2 block text-[11px] font-medium text-slate-400">
											{{ item.createdAt | date: 'medium' }}
										</span>
									</div>
								</ng-container>

								<!-- Fallback for future notification types -->
								<ng-container *ngSwitchDefault>
									<!-- Icon bubble -->
									<div
										class="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
										[ngClass]="{
											'bg-primary/10 text-primary': !item.isRead,
											'bg-slate-100 text-slate-400': item.isRead
										}"
									>
										<mat-icon
											fontSet="fas"
											fontIcon="fa-bell"
											class="!text-sm"
										></mat-icon>
									</div>

									<!-- Content -->
									<div class="min-w-0 flex-1">
										<span
											class="line-clamp-2 text-sm font-semibold leading-snug"
											[ngClass]="{
												'text-slate-950': !item.isRead,
												'text-slate-700': item.isRead
											}"
										>
											{{ item.title }}
										</span>

										<!-- Comment/body -->
										<p
											*ngIf="item.body"
											class="mt-1 line-clamp-2 text-xs leading-relaxed text-slate-500"
										>
											{{ item.body }}
										</p>

										<!-- Timestamp -->
										<span class="mt-2 block text-[11px] font-medium text-slate-400">
											{{ item.createdAt | date: 'MMM d, y, h:mm a' }}
										</span>
									</div>
								</ng-container>
							</ng-container>
						</div>
					</button>
				</div>
			</ng-container>

			<ng-template #empty>
				<div class="flex flex-col items-center justify-center px-6 py-10 text-center">
					<div class="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
						<mat-icon
							fontSet="fas"
							fontIcon="fa-bell"
							class="!text-xl !text-slate-400"
						></mat-icon>
					</div>

					<span class="text-sm font-medium text-slate-700">
						{{ 'userNotifications.empty' | transloco }}
					</span>

					<span class="mt-1 text-xs text-slate-400">
						{{ 'userNotifications.allCaughtUp' | transloco }}
					</span>
				</div>
			</ng-template>
		</ng-container>

		<ng-template #loading>
			<div class="flex items-center justify-center px-6 py-8">
				<span class="text-sm text-slate-400">
					{{ 'userNotifications.loading' | transloco }}
				</span>
			</div>
		</ng-template>
	</div>
</mat-menu>
